import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { PUBLIC_URL } from '../config/env';
import { canonicalSubject } from './progressStatus';

const VOCAB_BANK_FILES = {
  Math: 'Family Facing Support - Math Vocab Bank.csv',
  Literacy: 'Family Facing Support - Literacy Vocab Bank.csv',
};

// In-memory cache for parsed vocabulary banks keyed by canonical subject
const vocabCache = {};

function parseCsv(text) {
  const rows = [];
  let row = [];
  let field = '';
  let insideQuotes = false;
  for (let index = 0; index < text.length; index += 1) {
    const character = text[index];
    if (insideQuotes) {
      if (character === '"' && text[index + 1] === '"') {
        field += '"';
        index += 1;
      } else if (character === '"') {
        insideQuotes = false;
      } else {
        field += character;
      }
    } else if (character === '"') {
      insideQuotes = true;
    } else if (character === ',') {
      row.push(field);
      field = '';
    } else if (character === '\n') {
      row.push(field.replace(/\r$/, ''));
      rows.push(row);
      row = [];
      field = '';
    } else {
      field += character;
    }
  }
  if (field || row.length) {
    row.push(field.replace(/\r$/, ''));
    rows.push(row);
  }
  return rows;
}

export function indexVocabBank(csvText) {
  const [headerRow, ...dataRows] = parseCsv(csvText);
  const headers = (headerRow || []).map((header) => header.replace(/^\uFEFF/, '').replace(/\s+/g, ' ').trim().toLowerCase());
  const termIndex = headers.indexOf('term');
  const defIndex = headers.indexOf('definition');

  if (termIndex < 0 || defIndex < 0) {
    return {
      entries: new Map(),
      sortedEntries: [],
    };
  }

  const entries = new Map();
  dataRows.forEach((row) => {
    const rawTerm = String(row[termIndex] || '').trim();
    const definition = String(row[defIndex] || '').trim();
    if (!rawTerm || !definition) return;

    // Remove parenthetical notes from match key e.g. "factor (verb)" -> "factor"
    const matchTerm = rawTerm.replace(/\s*\([^)]*\)/g, '').trim();
    if (!matchTerm) return;

    const lowerKey = matchTerm.toLowerCase();
    if (!entries.has(lowerKey)) {
      entries.set(lowerKey, {
        term: rawTerm,
        matchTerm,
        definition,
      });
    }
  });

  // Sort terms by character length descending so multi-word terms (e.g. "equivalent fractions")
  // match before single-word substrings (e.g. "fractions")
  const sortedEntries = Array.from(entries.values()).sort(
    (a, b) => b.matchTerm.length - a.matchTerm.length || a.matchTerm.localeCompare(b.matchTerm),
  );

  return {
    entries,
    sortedEntries,
  };
}

export async function fetchVocabBank(subject) {
  const canon = canonicalSubject(subject);
  const fileName = VOCAB_BANK_FILES[canon];
  if (!fileName) return null;

  if (vocabCache[canon]) {
    return vocabCache[canon];
  }

  const response = await fetch(`${PUBLIC_URL}/${encodeURIComponent(fileName)}`);
  if (!response.ok) {
    throw new Error(`Failed to load ${canon} vocab bank (${response.status}).`);
  }
  const text = await response.text();
  const indexed = indexVocabBank(text);
  vocabCache[canon] = indexed;
  return indexed;
}

export async function fetchAllVocabBanks() {
  const results = {};
  await Promise.all(
    Object.keys(VOCAB_BANK_FILES).map(async (subj) => {
      try {
        results[subj] = await fetchVocabBank(subj);
      } catch (err: any) {
        console.error(`Could not load ${subj} vocab bank:`, err);
      }
    }),
  );
  return results;
}

function escapeRegex(str) {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function calculateBubbleCoords(triggerElement) {
  if (!triggerElement || typeof triggerElement.getBoundingClientRect !== 'function') {
    return { top: 0, left: 140, placement: 'top', arrowLeft: 140 };
  }
  const rect = triggerElement.getBoundingClientRect();
  const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 1024;
  const tooltipWidth = Math.min(290, Math.max(220, viewportWidth - 32));

  // If there's less than 130px above the trigger in viewport, show bubble below
  const placement = rect.top < 130 ? 'bottom' : 'top';

  const triggerCenterX = rect.left + rect.width / 2;
  const margin = 16;
  const halfWidth = tooltipWidth / 2;
  const left = Math.max(margin + halfWidth, Math.min(viewportWidth - margin - halfWidth, triggerCenterX));
  const top = placement === 'top' ? rect.top - 10 : rect.bottom + 10;
  const boxLeft = left - halfWidth;
  const arrowLeft = Math.max(20, Math.min(tooltipWidth - 20, triggerCenterX - boxLeft));

  return { top, left, placement, arrowLeft };
}

/**
 * VocabTermWithTooltip - renders a single highlighted vocabulary term with an accessible hover popup tooltip.
 * Renders via React Portal directly into document.body to guarantee top-layer visibility without clipping.
 */
export function VocabTermTooltip({ term, definition, children }: any) {
  const triggerRef = useRef<any>(null);
  const [hovered, setHovered] = useState<any>(false);
  const [coords, setCoords] = useState<any>(null);

  const updatePosition = useCallback(() => {
    if (triggerRef.current) {
      setCoords(calculateBubbleCoords(triggerRef.current));
    }
  }, []);

  const handleOpen = () => {
    setHovered(true);
    if (triggerRef.current) {
      setCoords(calculateBubbleCoords(triggerRef.current));
    }
  };

  const handleClose = () => {
    setHovered(false);
  };

  useEffect(() => {
    if (!hovered) return undefined;
    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [hovered, updatePosition]);

  const activeCoords = coords || (triggerRef.current ? calculateBubbleCoords(triggerRef.current) : { top: 0, left: 140, placement: 'top', arrowLeft: 140 });

  const tooltipContent = (
    <span
      role="tooltip"
      className="ae-vocab-tooltip ae-vocab-bubble"
      style={{
        position: 'fixed',
        top: activeCoords.top,
        left: activeCoords.left,
        transform: activeCoords.placement === 'top' ? 'translate(-50%, -100%)' : 'translate(-50%, 0)',
        minWidth: 220,
        maxWidth: 'min(320px, calc(100vw - 32px))',
        width: 'max-content',
        padding: '12px 16px',
        background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(240, 247, 255, 0.90) 100%)',
        backdropFilter: 'blur(16px) saturate(180%)',
        WebkitBackdropFilter: 'blur(16px) saturate(180%)',
        color: '#0f172a',
        borderRadius: 16,
        border: '1.5px solid rgba(255, 255, 255, 0.95)',
        outline: '1px solid rgba(191, 219, 254, 0.45)',
        boxShadow: '0 16px 36px -4px rgba(15, 23, 42, 0.16), 0 6px 18px -2px rgba(59, 130, 246, 0.10), inset 0 2px 2px rgba(255, 255, 255, 1), inset 0 -1.5px 2px rgba(191, 219, 254, 0.4)',
        fontSize: 12.5,
        lineHeight: 1.52,
        fontWeight: 450,
        textAlign: 'left',
        zIndex: 999999,
        pointerEvents: 'none',
        whiteSpace: 'normal',
        transition: 'transform 0.08s ease-out, opacity 0.12s ease-out',
      }}
    >
      {/* Air bubble pointer arrow */}
      <span
        aria-hidden="true"
        style={{
          position: 'absolute',
          bottom: activeCoords.placement === 'top' ? -6 : undefined,
          top: activeCoords.placement === 'bottom' ? -6 : undefined,
          left: activeCoords.arrowLeft,
          transform: 'translateX(-50%) rotate(45deg)',
          width: 12,
          height: 12,
          background: activeCoords.placement === 'top' ? 'rgba(240, 247, 255, 0.94)' : 'rgba(255, 255, 255, 0.95)',
          borderRight: activeCoords.placement === 'top' ? '1.5px solid rgba(191, 219, 254, 0.5)' : undefined,
          borderBottom: activeCoords.placement === 'top' ? '1.5px solid rgba(191, 219, 254, 0.5)' : undefined,
          borderLeft: activeCoords.placement === 'bottom' ? '1.5px solid rgba(255, 255, 255, 0.95)' : undefined,
          borderTop: activeCoords.placement === 'bottom' ? '1.5px solid rgba(255, 255, 255, 0.95)' : undefined,
          boxShadow: activeCoords.placement === 'top' ? '2px 2px 4px rgba(15, 23, 42, 0.04)' : '-2px -2px 4px rgba(15, 23, 42, 0.03)',
        }}
      />
      <div style={{ position: 'relative', zIndex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 5 }}>
          <strong style={{ display: 'block', color: '#1d4ed8', fontSize: 13, fontWeight: 800, letterSpacing: '-0.01em', textTransform: 'capitalize' }}>
            {term}
          </strong>
          <span style={{ fontSize: 9.5, fontWeight: 700, textTransform: 'uppercase', color: '#3b82f6', background: 'rgba(219, 234, 254, 0.8)', padding: '2px 7px', borderRadius: 999, letterSpacing: '0.04em' }}>
            Vocab
          </span>
        </div>
        <div style={{ color: '#1e293b', fontSize: 12.5, lineHeight: 1.52, fontWeight: 500 }}>
          {definition}
        </div>
      </div>
    </span>
  );

  const canUsePortal = typeof document !== 'undefined' && Boolean(document.body);

  return (
    <>
      <span
        ref={triggerRef}
        className="ae-vocab-term-wrapper"
        onMouseEnter={handleOpen}
        onMouseLeave={handleClose}
        onFocus={handleOpen}
        onBlur={handleClose}
        onClick={(e) => {
          e.stopPropagation();
        }}
        tabIndex={0}
        role="button"
        aria-label={`Vocabulary term: ${term}. Definition: ${definition}`}
        style={{
          position: 'relative',
          display: 'inline',
          cursor: 'help',
          borderBottom: '1.5px dotted #1d4ed8',
          color: 'inherit',
          fontWeight: 'inherit',
          outline: 'none',
          borderRadius: 2,
        }}
      >
        {children || term}
      </span>
      {hovered && (canUsePortal ? createPortal(tooltipContent, document.body) : tooltipContent)}
    </>
  );
}

/**
 * HighlightVocabText - scans text for terms from the subject's vocab bank and replaces matches with VocabTermTooltip.
 *
 * @param {string} text - text to render
 * @param {string} subject - subject name (e.g. "Literacy", "Math")
 * @param {object} vocabBank - indexed vocab bank for the subject
 * @param {boolean} active - whether vocab term highlighting is active (only for family view)
 */
export function HighlightVocabText({ text, subject, vocabBank, active = true }: any) {
  if (!text || typeof text !== 'string') return text || null;
  if (!active || !vocabBank || !vocabBank.sortedEntries || !vocabBank.sortedEntries.length) {
    return <>{text}</>;
  }

  const sortedTerms = vocabBank.sortedEntries;

  // Build a single regex pattern containing all terms separated by |
  // Bound by word boundaries or punctuation/space so subwords like "digit" in "digital" aren't matched.
  // Using lookaround (?<![\w\-]) and (?![\w\-]) for accurate word boundaries with hyphens.
  const patternParts = sortedTerms.map((entry) => escapeRegex(entry.matchTerm));
  const masterRegex = new RegExp(`(?<![\\w\\-])(${patternParts.join('|')})(?![\\w\\-])`, 'gi');

  const parts = [];
  let lastIndex = 0;
  let match;

  while ((match = masterRegex.exec(text)) !== null) {
    const matchStart = match.index;
    const matchText = match[0];
    const matchEnd = matchStart + matchText.length;

    if (matchStart > lastIndex) {
      parts.push(text.slice(lastIndex, matchStart));
    }

    const lowerMatch = matchText.toLowerCase();
    const entry = vocabBank.entries.get(lowerMatch) || sortedTerms.find((e) => e.matchTerm.toLowerCase() === lowerMatch);
    const definition = entry ? entry.definition : '';
    const displayTerm = entry ? entry.term : matchText;

    parts.push(
      <VocabTermTooltip
        key={`vocab-${matchStart}-${matchText}`}
        term={displayTerm}
        definition={definition}
      >
        {matchText}
      </VocabTermTooltip>,
    );

    lastIndex = matchEnd;
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return <>{parts}</>;
}
